import { type FC, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import {
  Button,
  type ButtonProps,
  Dialog,
  type DialogProps,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  useTheme,
  useMediaQuery,
  Typography,
  Box,
  type SxProps,
  type Theme,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { InfoCircle } from "iconsax-react";
import BDrawer from "../BDrawer";
import { RADIUS } from "../../theme/mixins";

interface ConfirmationDialogProps {
  open: boolean;
  title: string;
  message?: ReactNode;
  children?: ReactNode;
  confirmText?: string;
  cancelText?: string;
  confirmColor?: "primary" | "error" | "warning" | "inherit";
  confirmFirst?: boolean;
  showIcon?: boolean;
  iconColor?: "primary" | "error" | "warning" | "inherit";
  iconVariant?: "Outline" | "Bulk";
  iconSize?: number;
  showCloseButton?: boolean;
  maxWidth?: DialogProps["maxWidth"];
  cancelButtonProps?: Partial<ButtonProps>;
  confirmButtonProps?: Partial<ButtonProps>;
  paperSx?: SxProps<Theme>;
  titleSx?: SxProps<Theme>;
  titleTextSx?: SxProps<Theme>;
  contentSx?: SxProps<Theme>;
  messageTextSx?: SxProps<Theme>;
  actionsSx?: SxProps<Theme>;
  closeButtonSx?: SxProps<Theme>;
  onConfirm: () => void;
  onCancel: () => void;
}

const ConfirmationDialog: FC<ConfirmationDialogProps> = ({
  open,
  title,
  message,
  children,
  confirmText,
  cancelText,
  confirmColor = "inherit",
  confirmFirst = false,
  showIcon = true,
  iconColor,
  iconVariant = "Outline",
  iconSize = 24,
  showCloseButton = false,
  maxWidth = "xs",
  cancelButtonProps,
  confirmButtonProps,
  paperSx,
  titleSx,
  titleTextSx,
  contentSx,
  messageTextSx,
  actionsSx,
  closeButtonSx,
  onConfirm,
  onCancel,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down(1200));
  // Dutch UI: the defaults must not fall back to English (UI/UX audit 2.4.3).
  const resolvedConfirmText = confirmText ?? t("common.confirm");
  const resolvedCancelText = cancelText ?? t("common.cancel");

  const resolvedIconColor = (iconColor ?? confirmColor) === "error"
    ? theme.palette.error.main
    : (iconColor ?? confirmColor) === "warning"
    ? theme.palette.warning.main
    : (iconColor ?? confirmColor) === "primary"
    ? theme.palette.primary.main
    : theme.palette.primary.main;

  const cancelButton = (
    <Button key="cancel" fullWidth onClick={onCancel} variant="outlined" color="inherit" {...cancelButtonProps}>
      {resolvedCancelText}
    </Button>
  );

  const confirmButton = (
    <Button
      key="confirm"
      fullWidth
      onClick={onConfirm}
      variant="contained"
      color={confirmColor}
      {...confirmButtonProps}
    >
      {resolvedConfirmText}
    </Button>
  );

  const actions = confirmFirst ? (
    <>
      {confirmButton}
      {cancelButton}
    </>
  ) : (
    <>
      {cancelButton}
      {confirmButton}
    </>
  );

  const content = (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, pt: message || children ? 1 : 0 }}>
      {message && <Typography sx={{ color: "text.primary" }}>{message}</Typography>}
      {children}
    </Box>
  );

  if (isMobile) {
    return (
      <BDrawer
        open={open}
        onClose={onCancel}
        title={title}
        dialogActions={actions}
        showCloseButton
      >
        {content}
      </BDrawer>
    );
  }

  return (
    <Dialog
      open={open}
      onClose={onCancel}
      maxWidth={maxWidth}
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: `${RADIUS.cardLg}px`,
          boxShadow: theme.customShadows.modal,
          ...paperSx,
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          padding: "24px 12px 24px 24px",
          ...titleSx,
        }}
      >
        {showIcon && (
          <InfoCircle size={iconSize} variant={iconVariant} color={resolvedIconColor} style={{ flexShrink: 0 }} />
        )}
        <Box component="span" sx={{ flex: 1, ...titleTextSx }}>
          {title}
        </Box>
        {showCloseButton && (
          <IconButton
            size="small"
            onClick={onCancel}
            sx={{ flexShrink: 0, color: theme.palette.text.secondary, width: 28, height: 28, ...closeButtonSx }}
          >
            <CloseIcon sx={{ fontSize: 20 }} />
          </IconButton>
        )}
      </DialogTitle>

      <DialogContent sx={{ padding: '0px 24px 16px', paddingTop: '0px !important', ...contentSx }}>
        {message && (
          <DialogContentText sx={{ color: "text.primary", ...messageTextSx }}>
            {message}
          </DialogContentText>
        )}
        {children}
      </DialogContent>

      <DialogActions sx={{ p: "24px", gap: 1, ...actionsSx }}>
        {actions}
      </DialogActions>
    </Dialog>
  );
};

export default ConfirmationDialog;
